import { downloadMyData, updateProfile } from "api/auth";
import type { AccountDto } from "api/types";
import { fieldLimits } from "config";
import LineIcon from "pages/HomePage/components/LineIcon";
import React, { type ChangeEvent, type FormEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "Session";
import { formatPersonName, hasTooManyNameParts, maxNameParts } from "utilities";

import type { GeneralPanelProps } from "../settings.types";

import AvatarUploader from "./AvatarUploader";
import ChangeEmailDialog from "./ChangeEmailDialog";
import DeactivateAccountDialog from "./DeactivateAccountDialog";
import DeleteAccountDialog from "./DeleteAccountDialog";

import styles from "./GeneralPanel.module.css";

const GeneralPanel = ({
	displayName,
	email,
	emailConfirmed,
	initials,
	onSignOut,
	showEmailOnProfile
}: GeneralPanelProps) => {
	const { t } = useTranslation("settingsPage");
	const { getAccessToken, refreshUser } = useSession();

	const [fullName, setFullName] = useState(displayName);
	const [savedName, setSavedName] = useState(displayName);
	const [savedEmail, setSavedEmail] = useState(email);
	const [confirmed, setConfirmed] = useState(emailConfirmed);
	const [isSaving, setIsSaving] = useState(false);
	const [nameSaved, setNameSaved] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [emailDialogOpen, setEmailDialogOpen] = useState(false);
	const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [isExporting, setIsExporting] = useState(false);
	const [exportError, setExportError] = useState("");
	const [emailChanged, setEmailChanged] = useState(false);
	const [showEmail, setShowEmail] = useState(showEmailOnProfile);
	const [isPrivacySaving, setIsPrivacySaving] = useState(false);
	const [privacyError, setPrivacyError] = useState("");

	const isDirty = fullName.trim() !== savedName.trim();

	const onNameChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>): void => setFullName(event.target.value),
		[]
	);

	// The API stores the canonical "Firstname M Lastname" form, so show it as soon as the user leaves
	// the field rather than surprising them with a different name after saving. Too many parts is left
	// alone so the save below can report it instead of silently dropping one.
	const onNameBlur = useCallback(
		(): void =>
			setFullName((current: string) => (hasTooManyNameParts(current) ? current : formatPersonName(current))),
		[]
	);

	const onSubmit = useCallback(
		async (event: FormEvent<HTMLFormElement>): Promise<void> => {
			event.preventDefault();
			if (isSaving) {
				return;
			}

			if (hasTooManyNameParts(fullName)) {
				setErrorMessage(t("general.nameTooManyParts", { count: maxNameParts }));

				return;
			}

			const trimmedName = formatPersonName(fullName);
			if (!trimmedName) {
				setErrorMessage(t("general.nameRequired"));

				return;
			}

			setErrorMessage("");
			setNameSaved(false);
			setIsSaving(true);
			try {
				const token = await getAccessToken();
				if (!token) {
					return;
				}

				const updated = await updateProfile({ fullName: trimmedName }, token);
				setSavedName(updated.fullName);
				setFullName(updated.fullName);
				setNameSaved(true);

				await refreshUser();
			} catch (error) {
				setErrorMessage(error instanceof Error ? error.message : t("general.saveError"));
			} finally {
				setIsSaving(false);
			}
		},
		[fullName, getAccessToken, isSaving, refreshUser, t]
	);

	// The toggle saves immediately (optimistically, reverting on failure) — there is no save button.
	const onShowEmailToggle = useCallback(async (): Promise<void> => {
		if (isPrivacySaving) {
			return;
		}

		const nextValue = !showEmail;
		setPrivacyError("");
		setShowEmail(nextValue);
		setIsPrivacySaving(true);
		try {
			const token = await getAccessToken();
			if (!token) {
				return;
			}

			// The profile endpoint requires the name; send the saved one so pending edits stay pending.
			const updated = await updateProfile({ fullName: savedName.trim(), showEmailOnProfile: nextValue }, token);
			setShowEmail(updated.showEmailOnProfile);

			await refreshUser();
		} catch (error) {
			setShowEmail(!nextValue);
			setPrivacyError(error instanceof Error ? error.message : t("general.privacySaveError"));
		} finally {
			setIsPrivacySaving(false);
		}
	}, [getAccessToken, isPrivacySaving, refreshUser, savedName, showEmail, t]);

	const openEmailDialog = useCallback((): void => {
		setEmailChanged(false);
		setEmailDialogOpen(true);
	}, []);

	const closeEmailDialog = useCallback((): void => setEmailDialogOpen(false), []);

	const openDeactivateDialog = useCallback((): void => setDeactivateDialogOpen(true), []);
	const closeDeactivateDialog = useCallback((): void => setDeactivateDialogOpen(false), []);

	const openDeleteDialog = useCallback((): void => setDeleteDialogOpen(true), []);
	const closeDeleteDialog = useCallback((): void => setDeleteDialogOpen(false), []);

	// "Download my data": the API returns one JSON file with the account and billing details (TTAPI-50).
	const onDownloadData = useCallback(async (): Promise<void> => {
		if (isExporting) {
			return;
		}

		setExportError("");
		setIsExporting(true);
		try {
			const token = await getAccessToken();
			if (token) {
				await downloadMyData(token);
			}
		} catch (error) {
			setExportError(error instanceof Error ? error.message : t("general.downloadDataError"));
		} finally {
			setIsExporting(false);
		}
	}, [getAccessToken, isExporting, t]);

	const onEmailChanged = useCallback(
		(account: AccountDto): void => {
			setSavedEmail(account.email ?? "");
			setConfirmed(account.emailConfirmed);
			setEmailDialogOpen(false);
			setEmailChanged(true);
			refreshUser().catch(() => undefined);
		},
		[refreshUser]
	);

	return (
		<div className={styles.panel}>
			<section className={styles.profileCard}>
				<AvatarUploader initials={initials} />
				<div className={styles.profileText}>
					<span className={styles.profileName}>{savedName}</span>
					<span className={styles.profileEmail}>{savedEmail || t("general.noEmail")}</span>
				</div>
			</section>

			<form className={styles.detailsCard} onSubmit={onSubmit}>
				<h3 className={styles.cardTitle}>{t("general.accountDetails")}</h3>

				<label className={styles.field} htmlFor="settings-fullName">
					<span className={styles.fieldLabel}>{t("general.fullName")}</span>
					<input
						autoComplete="name"
						className={styles.input}
						disabled={isSaving}
						id="settings-fullName"
						maxLength={fieldLimits.fullName}
						onBlur={onNameBlur}
						onChange={onNameChange}
						type="text"
						value={fullName}
					/>
				</label>

				<div className={styles.field}>
					<span className={styles.fieldLabel}>
						{t("general.email")}
						<span
							className={`${styles.statusBadge} ${
								confirmed ? styles.statusVerified : styles.statusUnverified
							}`}>
							{confirmed ? t("general.verified") : t("general.unverified")}
						</span>
					</span>
					<div className={styles.emailRow}>
						<span className={styles.emailValue}>{savedEmail || t("general.noEmail")}</span>
						<button className={styles.changeButton} onClick={openEmailDialog} type="button">
							{t("general.changeEmail")}
						</button>
					</div>
				</div>

				{emailChanged && <p className={styles.noticeSuccess}>{t("general.emailChanged")}</p>}
				{nameSaved && <p className={styles.noticeSuccess}>{t("general.saved")}</p>}
				{errorMessage && <p className={styles.noticeError}>{errorMessage}</p>}

				<div className={styles.formActions}>
					<button className={styles.saveButton} disabled={isSaving || !isDirty} type="submit">
						{isSaving ? t("general.saving") : t("general.save")}
					</button>
				</div>
			</form>

			<section className={styles.detailsCard}>
				<h3 className={styles.cardTitle}>{t("general.privacy")}</h3>

				<div className={styles.privacyRow}>
					<div className={styles.privacyText}>
						<span className={styles.privacyLabel}>{t("general.showEmailLabel")}</span>
						<span className={styles.privacyDescription}>{t("general.showEmailDescription")}</span>
					</div>
					<label className={styles.switch} htmlFor="settings-showEmailOnProfile">
						<input
							checked={showEmail}
							className={styles.switchInput}
							disabled={isPrivacySaving}
							id="settings-showEmailOnProfile"
							onChange={onShowEmailToggle}
							type="checkbox"
						/>
						<span aria-hidden="true" className={styles.switchTrack} />
						<span className={styles.switchLabel}>{showEmail ? t("general.on") : t("general.off")}</span>
					</label>
				</div>

				{privacyError && <p className={styles.noticeError}>{privacyError}</p>}
			</section>

			<button className={styles.signOutButton} onClick={onSignOut} type="button">
				<LineIcon name="logout" />
				<span>{t("general.signOut")}</span>
			</button>

			<section className={styles.detailsCard}>
				<h3 className={styles.cardTitle}>{t("general.yourData")}</h3>

				<div className={styles.dangerRow}>
					<div className={styles.dangerText}>
						<span className={styles.dangerLabel}>{t("general.downloadDataLabel")}</span>
						<span className={styles.dangerDescription}>{t("general.downloadDataDescription")}</span>
					</div>
					<button
						className={styles.changeButton}
						disabled={isExporting}
						onClick={onDownloadData}
						type="button">
						{isExporting ? t("general.downloadDataPreparing") : t("general.downloadDataButton")}
					</button>
				</div>

				{exportError && <p className={styles.noticeError}>{exportError}</p>}
			</section>

			<section className={`${styles.detailsCard} ${styles.dangerCard}`}>
				<h3 className={`${styles.cardTitle} ${styles.dangerTitle}`}>{t("general.dangerZone")}</h3>

				<div className={styles.dangerRow}>
					<div className={styles.dangerText}>
						<span className={styles.dangerLabel}>{t("general.deactivateLabel")}</span>
						<span className={styles.dangerDescription}>{t("general.deactivateDescription")}</span>
					</div>
					<button className={styles.deactivateButton} onClick={openDeactivateDialog} type="button">
						{t("general.deactivateButton")}
					</button>
				</div>

				<div className={styles.dangerRow}>
					<div className={styles.dangerText}>
						<span className={styles.dangerLabel}>{t("general.deleteLabel")}</span>
						<span className={styles.dangerDescription}>{t("general.deleteDescription")}</span>
					</div>
					<button className={styles.deactivateButton} onClick={openDeleteDialog} type="button">
						{t("general.deleteButton")}
					</button>
				</div>
			</section>

			{emailDialogOpen && (
				<ChangeEmailDialog currentEmail={savedEmail} onChanged={onEmailChanged} onClose={closeEmailDialog} />
			)}

			{deactivateDialogOpen && <DeactivateAccountDialog onClose={closeDeactivateDialog} />}

			{deleteDialogOpen && <DeleteAccountDialog onClose={closeDeleteDialog} />}
		</div>
	);
};

export default GeneralPanel;
